/**
 * MUTAGIM.SHOP — Admin Behavioral Analytics Page (Phase 19)
 * Analyse comportementale des utilisateurs
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, ShoppingCart, Search, Eye, Clock, TrendingDown,
  AlertTriangle, Package, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminBehaviorPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState('');
  const [data, setData] = useState({
    abandoned_cart_products: [],
    failed_searches: [],
    top_pages: [],
    avg_session_duration: '0m 0s',
    bounce_rate: 0
  });

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      fetchData(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchData = async (authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/engagement/analytics/behavior`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <AdminPageWrapper title="Données Comportementales" subtitle="Analyse du comportement utilisateur">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-mutagim-red border-t-transparent rounded-full" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper title="Données Comportementales" subtitle="Analyse du comportement utilisateur">
      <div data-testid="admin-behavior-page">
        {/* KPIs Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Temps moyen sur le site</p>
                  <p className="text-2xl font-bold">{data.avg_session_duration}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taux de rebond</p>
                  <p className="text-2xl font-bold">{data.bounce_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Paniers abandonnés</p>
                  <p className="text-2xl font-bold">{data.abandoned_cart_products.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Abandoned Cart Products */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-orange-500" />
                Produits abandonnés au panier
              </CardTitle>
              <CardDescription>
                Produits souvent ajoutés au panier mais non achetés
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.abandoned_cart_products.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-3">
                  {data.abandoned_cart_products.map((product, idx) => (
                    <div 
                      key={product.product_id || idx}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center text-orange-600 font-bold">
                          {idx + 1}
                        </div>
                        <span className="font-medium">{product.name || 'Produit inconnu'}</span>
                      </div>
                      <Badge variant="secondary">
                        {product.cart_adds} ajouts
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Failed Searches */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5 text-red-500" />
                Recherches sans résultats
              </CardTitle>
              <CardDescription>
                Termes recherchés qui n'ont rien trouvé — opportunités produits
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.failed_searches.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucune recherche échouée</p>
              ) : (
                <div className="space-y-2">
                  {data.failed_searches.map((search, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 border-b last:border-0"
                    >
                      <span className="text-gray-700">"{search.term}"</span>
                      <Badge variant="outline" className="text-red-600">
                        {search.count}x
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Pages */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-500" />
                Pages les plus visitées
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_pages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              ) : (
                <div className="space-y-3">
                  {data.top_pages.slice(0, 10).map((page, idx) => {
                    const maxViews = data.top_pages[0]?.views || 1;
                    const percentage = (page.views / maxViews) * 100;
                    
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium truncate max-w-md">
                            {page.page || '/'}
                          </span>
                          <span className="text-gray-500">{page.views} vues</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminBehaviorPage;
